import { Injectable } from '@angular/core';

import { CalculationResult, OperationPoint } from '../models';

const MAX_FREQUENCY = 100;
type ObjectKey = keyof OperationPoint;

@Injectable({
  providedIn: 'root',
})
export class OperationPointDisplayService {
  constructor() {}

  public getOperationPointDisplay(calculationResult: CalculationResult | null = null) {
    const display = this.getDefaultDisplay();

    if (calculationResult === null || calculationResult?.operationPoints?.length === 0) {
      return display;
    }

    display.minOverallEfficiency = this.getMinimumOperationPoints(
      calculationResult.operationPoints,
      'overallEfficiency'
    );
    display.minMechanicalEfficiency = this.getMinimumOperationPoints(
      calculationResult.operationPoints,
      'mechanicalEfficiency'
    );
    display.minVolumetricEfficiency = this.getMinimumOperationPoints(
      calculationResult.operationPoints,
      'volumetricEfficiency'
    );
    display.maxOperatingTorque = this.getMaximumOperationPoints(calculationResult.operationPoints, 'operatingTorque');
    display.maxNpsh = this.getMaximumOperationPoints(calculationResult.operationPoints, 'npshr');
    display.maxSpeedAt100VolumeetricEfficiency = this.getMaximumOperationPoints(
      calculationResult.operationPoints,
      'speedAt100VolumetricEfficiency'
    );
    // TODO: Starting power! Operating Power display

    const normalOperationPoint = calculationResult.operationPoints.find((en) => en.name === 'normal');
    if (normalOperationPoint) {
      const pumpSpeed = normalOperationPoint.pumpSpeed;
      const minPumpSpeed = normalOperationPoint.allowedSpeedMin;
      const maxPumpSpeed = normalOperationPoint.allowedSpeedMax;
      const frequency = normalOperationPoint.frequency ?? 0;

      if (pumpSpeed && minPumpSpeed && maxPumpSpeed) {
        display.validPumpSpeedNormal = pumpSpeed >= minPumpSpeed && pumpSpeed <= maxPumpSpeed;
      }

      display.validFrequencyNormal = frequency < MAX_FREQUENCY;
      display.validDifferentialPressureNormal = !normalOperationPoint.isDifferentialPressureExceeded;
    }

    const minOperationPoint = calculationResult.operationPoints.find((en) => en.name === 'min');
    if (minOperationPoint) {
      const pumpSpeed = minOperationPoint.pumpSpeed;
      const minPumpSpeed = minOperationPoint.allowedSpeedMin;
      const maxPumpSpeed = minOperationPoint.allowedSpeedMax;
      const frequency = minOperationPoint.frequency ?? 0;

      if (pumpSpeed && minPumpSpeed && maxPumpSpeed) {
        display.validPumpSpeedMin = pumpSpeed >= minPumpSpeed && pumpSpeed <= maxPumpSpeed;
      }

      display.validFrequencyMin = frequency < MAX_FREQUENCY;
      display.validDifferentialPressureMin = !minOperationPoint.isDifferentialPressureExceeded;
    }

    const maxOperationPoint = calculationResult.operationPoints.find((en) => en.name === 'max');
    if (maxOperationPoint) {
      const pumpSpeed = maxOperationPoint.pumpSpeed;
      const minPumpSpeed = maxOperationPoint.allowedSpeedMin;
      const maxPumpSpeed = maxOperationPoint.allowedSpeedMax;
      const frequency = maxOperationPoint.frequency ?? 0;

      if (pumpSpeed && minPumpSpeed && maxPumpSpeed) {
        display.validPumpSpeedMax = pumpSpeed >= minPumpSpeed && pumpSpeed <= maxPumpSpeed;
      }

      display.validFrequencyMax = frequency < MAX_FREQUENCY;
      display.validDifferentialPressureMax = !maxOperationPoint.isDifferentialPressureExceeded;
    }

    const ratedOperationPoint = calculationResult.operationPoints.find((en) => en.name === 'rated');
    if (ratedOperationPoint) {
      const pumpSpeed = ratedOperationPoint.pumpSpeed;
      const minPumpSpeed = ratedOperationPoint.allowedSpeedMin;
      const maxPumpSpeed = ratedOperationPoint.allowedSpeedMax;
      const frequency = ratedOperationPoint.frequency ?? 0;

      if (pumpSpeed && minPumpSpeed && maxPumpSpeed) {
        display.validPumpSpeedRated = pumpSpeed >= minPumpSpeed && pumpSpeed <= maxPumpSpeed;
      }

      display.validFrequencyRated = frequency < MAX_FREQUENCY;
      display.validDifferentialPressureRated = !ratedOperationPoint.isDifferentialPressureExceeded;
    }

    return display;
  }

  private getMinimumOperationPoints(operationPoints: OperationPoint[], key: ObjectKey) {
    let currentMinimum: number | null = null;
    let minOperationPoints = '';

    for (const operationPoint of operationPoints) {
      if (operationPoint.name === 'rated' || operationPoint.isDifferentialPressureExceeded) {
        continue;
      }

      const value: number = operationPoint[key] as number;
      if (currentMinimum == null || currentMinimum > value) {
        currentMinimum = value;
        minOperationPoints = operationPoint.name;
        continue;
      }

      if (currentMinimum === value) {
        minOperationPoints += ',' + operationPoint.name;
      }
    }

    return minOperationPoints;
  }

  private getMaximumOperationPoints(operationPoints: OperationPoint[], key: ObjectKey) {
    let currentMaximum: number | null = null;
    let minOperationPoints = '';

    for (const operationPoint of operationPoints) {
      if (operationPoint.name === 'rated' || operationPoint.isDifferentialPressureExceeded) {
        continue;
      }

      const value: number = operationPoint[key] as number;
      if (currentMaximum == null || currentMaximum < value) {
        currentMaximum = value;
        minOperationPoints = operationPoint.name;
        continue;
      }

      if (currentMaximum === value) {
        minOperationPoints += ',' + operationPoint.name;
      }
    }

    return minOperationPoints;
  }

  private getDefaultDisplay() {
    return {
      validPumpSpeedNormal: true,
      validPumpSpeedMin: true,
      validPumpSpeedMax: true,
      validPumpSpeedRated: true,
      validDifferentialPressureNormal: true,
      validDifferentialPressureMin: true,
      validDifferentialPressureMax: true,
      validDifferentialPressureRated: true,
      validFrequencyNormal: true,
      validFrequencyMin: true,
      validFrequencyMax: true,
      validFrequencyRated: true,
      minOverallEfficiency: '',
      minMechanicalEfficiency: '',
      minVolumetricEfficiency: '',
      maxOperatingTorque: '',
      maxNpsh: '',
      maxSpeedAt100VolumeetricEfficiency: '',
    };
  }
}
